// Shared helpers used across tour definitions.

export const NEXT  = { text: 'Next', action() { return this.next(); } };
export const BACK  = { text: 'Previous', secondary: true, action() { return this.back(); } };
export const START = { text: 'Start', action() { return this.next(); } };
export const SKIP  = { text: 'Skip', secondary: true, action() { return this.cancel(); } };
export const DONE  = {
    text: 'Got it',
    action() {
        // Mark the tour as seen and tear down the tooltip first.
        this.complete();
        // Then drop the user back on the Help page so they can pick another
        // walkthrough or replay this one. Aml.vue exposes its setPage on
        // window during mount; if it's missing for any reason we silently
        // no-op and just leave the user where they ended.
        try {
            if (typeof window.amlSetPage === 'function') {
                window.amlSetPage('help-walkthroughs');
            }
        } catch (e) { /* ignore */ }
    },
};

// Use as a step's `showOn` to skip the step when its target is not in the DOM.
// Useful for explainer tours where some elements are role-gated (e.g. management-only
// dashboard cards or sidebar items the current user cannot see).
export function targetExists(selector) {
    return () => !!document.querySelector(selector);
}

/**
 * Returns a Shepherd `when` hook that advances the tour the moment a
 * matching element for `nextSelector` appears in the DOM.
 *
 * Use this in place of `advanceOn: { event: 'click' }` when you'd
 * otherwise hit one of these footguns:
 *
 *   - The selector matches multiple elements (e.g. every row of a
 *     clickable table). Shepherd's advanceOn uses `document.querySelector`
 *     and attaches to only the first match, so clicks on later rows
 *     never advance the tour.
 *   - The user might reach the destination by some other path
 *     (notification click, URL paste, "back" button) — watching for
 *     the destination marker is path-agnostic.
 *   - Validation may block the click action (e.g. wizard Continue),
 *     and you want the tour to wait for the actual transition.
 */
export function advanceWhenAppears(nextSelector) {
    return {
        show() {
            const tour = this.tour;
            const stepInstance = this;
            let observer = null;
            let poll = null;
            let armed = false;
            setTimeout(() => { armed = true; }, 50);

            const teardown = () => {
                if (observer) { observer.disconnect(); observer = null; }
                if (poll) { clearInterval(poll); poll = null; }
                stepInstance._advanceWatch = null;
            };

            const check = () => {
                let current = null;
                try { current = tour.getCurrentStep && tour.getCurrentStep(); }
                catch (e) { /* ignore */ }
                if (current !== stepInstance) {
                    teardown();
                    return;
                }
                if (armed && document.querySelector(nextSelector)) {
                    teardown();
                    try { tour.next(); } catch (e) { /* ignore */ }
                }
            };

            observer = new MutationObserver(check);
            observer.observe(document.body, { childList: true, subtree: true });
            // Belt-and-braces poll in case a mutation batch is coalesced
            // and the observer misses the addition. Cheap (200ms tick).
            poll = setInterval(check, 200);
            stepInstance._advanceWatch = teardown;
        },
        hide() {
            if (this._advanceWatch) this._advanceWatch();
        },
    };
}
